import 'react-phone-input-2/lib/style.css';
import './SignUpForm.scss';

import {
  AbsoluteRoutes,
  NotificationType,
  allowedCountriesList,
  getAgreementUrl,
  post,
  useAddNotification,
  useTranslation,
} from '../../utils';
import { ErrorMessage, Field, Form, useFormikContext } from 'formik';
import type { TCities, TForm } from '../../utils/types';
import { useEffect, useState } from 'react';

import { CitySelector } from '../CitySelector';
import { NavLink } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import { activeRequestsAtom } from '../../atoms';
import { useSetRecoilState } from 'recoil';

type TCitiesPage = {
  content: TCities[];
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
};

const SignUpForm = () => {
  const [country, setCountry] = useState<string>('');
  const [cities, setCities] = useState<TCities[] | undefined>();
  const setActiveRequests = useSetRecoilState(activeRequestsAtom);
  const { setFieldValue, values, errors, submitCount } = useFormikContext<TForm>();
  const [isPhoneFocused, setIsPhoneFocused] = useState(false);

  const showFullNameError = submitCount > 0 && !!errors.fullName && !values.fullName;
  const showPhoneError = submitCount > 0 && !!errors.phoneNumber && !values.phoneNumber;
  const showEmailError = submitCount > 0 && !!errors.email && !values.email;

  const { t } = useTranslation();
  const pushNotification = useAddNotification();

  async function getCities(code: string, name: string) {
    const upperCaseCode = code.toLocaleUpperCase();
    if (upperCaseCode === country) return;
    setActiveRequests(prev => prev + 1);
    setFieldValue('country', name);

    const { data, error } = await post<{ countryIso: string; cityName: string }, TCitiesPage>(
      'CITIES',
      { countryIso: upperCaseCode, cityName: '' },
      '?lang=en&page=0&size=500&sortBy=name&order=ASC',
    );

    if (!!data) {
      setCities(data.content);
    } else {
      console.log(error);
      pushNotification({
        type: NotificationType.Error,
        title: t('Web.Title_load_error'),
        text: t('Web.Error_cities_load_failed'),
      });
    }
    setActiveRequests(prev => prev - 1);
    setCountry(upperCaseCode);
  }

  useEffect(() => {
    getCities('US', 'United States'); // as a default value - TEMPORARY
    // only when mounting the component
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <h2 className='signUpFormHeader'>
        <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
          <circle cx='9' cy='7' r='4' />
          <path d='M2 21c0-4 3-7 7-7s7 3 7 7' strokeLinecap='round' strokeLinejoin='round' />
          <path d='M19 8v6M22 11h-6' strokeLinecap='round' strokeLinejoin='round' />
        </svg>
        {t('Verification 2.Button_sign_up')}
      </h2>
      <Form className='signUpForm'>
        <div className='form-group'>
          <label htmlFor='fullName'>
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
              <circle cx='12' cy='8' r='4' />
              <path d='M4 21c0-4 3.5-7 8-7s8 3 8 7' strokeLinecap='round' strokeLinejoin='round' />
            </svg>
            {t('Profile (expert).Text_full_name')}
          </label>
          <Field
            id='fullName'
            name='fullName'
            className={`form-control${showFullNameError ? ' error' : ''}`}
            placeholder={showFullNameError ? errors.fullName : t('Sign up (client).Username')}
          />
          {!showFullNameError && <ErrorMessage name='fullName' component='div' className='error-message' />}
        </div>

        <div className='form-group'>
          <label htmlFor='country'>
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
              <path
                d='M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.68 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.32 1.85.55 2.81.68A2 2 0 0 1 22 16.92z'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>

            {t('Web.Text_full_number')}
          </label>
          <div className='phone-input-wrap'>
            <PhoneInput
              country='us'
              onlyCountries={allowedCountriesList}
              autoFormat={false}
              inputProps={{
                name: 'phone',
                required: true,
                onFocus: () => setIsPhoneFocused(true),
                onBlur: () => setIsPhoneFocused(false),
              }}
              onChange={(phone, country) => {
                setFieldValue('phoneNumber', `+${phone}`);
                //@ts-ignore
                getCities(country.countryCode, country.name);
              }}
              containerClass='phone-input'
              inputClass={`form-control${showPhoneError ? ' error' : ''}`}
            />
            {showPhoneError && !isPhoneFocused && (
              <span className='phone-input-placeholder error'>{errors.phoneNumber}</span>
            )}
            {!showPhoneError && !isPhoneFocused && !values.phoneNumber && (
              <span className='phone-input-placeholder'>{t('Web.Placeholder_phone_number')}</span>
            )}
          </div>
          {!showPhoneError && <ErrorMessage name='phoneNumber' component='div' className='error-message' />}
        </div>

        {!!cities && (
          <div className='form-group'>
            <Field component={CitySelector} name='cityCode' options={cities} initialValue='' />
            {!(submitCount > 0 && !!errors.cityCode && !values.cityCode) && (
              <ErrorMessage name='cityCode' component='div' className='error-message' />
            )}
          </div>
        )}

        <div className='form-group'>
          <label htmlFor='email'>
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
              <rect x='2' y='4' width='20' height='16' rx='2' />
              <path d='M2 7l10 6 10-6' strokeLinecap='round' strokeLinejoin='round' />
            </svg>
            {t('Sign up (client).Field_email')}
          </label>
          <Field
            id='email'
            name='email'
            type='email'
            className={`form-control${showEmailError ? ' error' : ''}`}
            placeholder={showEmailError ? errors.email : t('Sign up (client).Example_email')}
          />
          {!showEmailError && <ErrorMessage name='email' component='div' className='error-message' />}
        </div>

        <div className='agreementGroup'>
          <label className='agreementLabel' htmlFor='agreedToTerms'>
            <Field type='checkbox' id='agreedToTerms' name='agreedToTerms' />
            <span className='agreementCheckmark' />
            <span className='agreementText'>
              {t('Web.Agreement_prefix')}{' '}
              <a
                href={getAgreementUrl()}
                target='_blank'
                rel='noopener noreferrer'
                onClick={e => e.stopPropagation()}>
                {t('Web.Agreement_link')}
              </a>
            </span>
          </label>
          <ErrorMessage name='agreedToTerms' component='div' className='error-message' />
        </div>

        <button type='submit' disabled={!values.agreedToTerms}>
          {t('Verification 2.Button_sign_up')}
        </button>
      </Form>
      <div className='signUpBottomLink'>
        <p>{t('Web.Text_already_have_account_web')}</p>
        <NavLink to={AbsoluteRoutes.login}>{t('Web.Text_go_to_login_web')}</NavLink>
      </div>
    </>
  );
};

export default SignUpForm;